class PiranhaHeroSlider extends HTMLElement {
  constructor() {
    super();
    this.swiperId = this.id;
    this.dataSelector = this.getAttribute("data-selector");
    this.dataTargetSelector = this.getAttribute("data-selector");
    this.dataAutoplayDelay = Number(this.dataset.autoplayDelay ?? 4000);
    
    // Cache for performance
    this.swiperInstance = null;
    this.isInitializing = false;
    this.isInitialized = false;
    this.observer = null;
    
    // Cache DOM elements - will be set after DOM is ready
    this.swiperDiv = null;
    this.dataSliderContent = null;
    
    // Cache swiper options
    this._cachedOptions = null;
    
    // Bind methods for better performance
    this.initSwiperInstance = this.initSwiperInstance.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
  }

  connectedCallback() {
    // Cache DOM elements once on connection
    this.swiperDiv = this.querySelector(`.${this.dataSelector}`);
    this.dataSliderContent = this.querySelector(".hero-slider-content-wrapper");
    
    this.initLazyLoading();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initLazyLoading() {
    if (this.isInitialized || this.isInitializing) return;
    
    // More efficient intersection observer
    const targetElement = document.querySelector(`.${this.dataTargetSelector}`);
    if (!targetElement) {
      console.warn("Target element not found for lazy loading:", this.dataTargetSelector);
      return;
    }

    this.observer = new IntersectionObserver(this.handleIntersection, {
      root: null,
      rootMargin: "100px 0px 0px 0px",
      threshold: 0
    });

    this.observer.observe(targetElement);
    
    // Fallback: Initialize after a reasonable delay if not intersected
    setTimeout(() => {
      if (!this.isInitialized && !this.isInitializing) {
        this.initSwiperInstance();
      }
    }, 3000);
  }

  handleIntersection(entries) {
    const entry = entries[0];
    if (entry.isIntersecting && !this.isInitialized && !this.isInitializing) {
      console.debug("Creating optimized hero slider instance...");
      this.initSwiperInstance();
      
      // Stop observing immediately
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  initSwiperInstance() {
    if (this.isInitialized || this.isInitializing) return;
    
    this.isInitializing = true;
    
    const selectorString = `.${this.dataSelector}`;
    const swiperElement = document.querySelector(selectorString);
    
    if (!swiperElement) {
      console.warn("Hero slider element not found:", selectorString);
      this.isInitializing = false;
      return;
    }

    // Cleanup any existing instance
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    // Use requestAnimationFrame for smoother initialization
    requestAnimationFrame(() => {
      try {
        this.swiperInstance = new Swiper(selectorString, this.getSwiperOptions());
        this.isInitialized = true;
      } catch (error) {
        console.error("Failed to initialize Hero Slider:", error);
      } finally {
        this.isInitializing = false;
      }
    });
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    
    this.isInitialized = false;
    this.isInitializing = false;
  }

  getSwiperOptions = () => {
    // Cache options to avoid recreating the object every time
    if (this._cachedOptions) {
      return this._cachedOptions;
    }

    this._cachedOptions = {
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      },
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      centeredSlides: true,
      autoplay: {
        delay: this.dataAutoplayDelay,
        disableOnInteraction: false,
      },
      mousewheel: { forceToAxis: true },
      grabCursor: true,
      updateOnWindowResize: true,
      observer: false,
      observeParents: false,
      runCallbacksOnInit: false,
      on: {
        slideChange: (slide) => {
          if (!this.dataSliderContent) return;
          
          const previousContentSlide = this.dataSliderContent.children.item(
            slide.previousIndex
          );
          const activeContentSlide = this.dataSliderContent.children.item(
            slide.activeIndex
          );

          if (previousContentSlide) {
            previousContentSlide.setAttribute("data-active", "false");
          }
          if (activeContentSlide) {
            activeContentSlide.setAttribute("data-active", "true");
          }
        },
      },
    };

    return this._cachedOptions;
  };
}

// Enhanced event handling for Shopify theme editor
document.addEventListener("shopify:section:load", (event) => {
  const heroSliders = event.target.querySelectorAll('piranha-hero-slider');
  heroSliders.forEach(slider => {
    if (slider.swiperInstance) {
      slider.cleanup();
      slider.connectedCallback();
    }
  });
});

document.addEventListener("shopify:section:unload", (event) => {
  const heroSliders = event.target.querySelectorAll('piranha-hero-slider');
  heroSliders.forEach(slider => {
    slider.cleanup();
  });
});

customElements.define("piranha-hero-slider", PiranhaHeroSlider);
